import type { BoxCollider, StairDef, ZoneDef } from '../world/plan';
import type { Vec3 } from '../sim/types';

/**
 * NAVIGATION.
 *
 * Guards need to get from the control room to the jewellery store, up an
 * escalator, around a planter and through a shutter that was not there when the
 * round started. Nothing else in the game needs to know how the building
 * connects, so this is the only place that works it out.
 *
 * WHY A HEIGHT-FIELD GRID AND NOT A NAVMESH. The mall is authored as
 * axis-aligned boxes and it never changes shape, so the walkable surface is
 * fully determined by the collider list — there is nothing to author by hand and
 * nothing to keep in sync. A polygon navmesh would be smaller and prettier, and
 * would cost a day of convex decomposition to produce exactly the same paths. A
 * plain 2D grid would be simpler still, and would be wrong: the building has two
 * floors stacked on the same footprint, and a flat grid would happily route a
 * guard through the gallery floor into the atrium below. So each cell holds a
 * short list of walkable HEIGHTS — the ground, the gallery, and every step of
 * the ramp in between — and pathfinding runs over (cell, height) pairs. Stairs
 * and escalators then need no special case at all: consecutive ramp cells differ
 * by less than a step, so they connect like any other neighbours.
 *
 * WHY THE AGENT IS MODELLED SLIGHTLY THIN. Inflating obstacles by the full
 * capsule radius, at any grid resolution coarse enough to build in a few
 * milliseconds, closes every 1.1m service door in the building — the free strip
 * down the middle of the doorway ends up narrower than one cell and no sample
 * lands in it. Guards steer with the same collide-and-slide controller the
 * player uses, so a path that clips a doorframe is recovered from rather than
 * fatal. The grid is therefore deliberately a little optimistic, and the
 * controller is what makes it true.
 */

/** Grid pitch in metres. 0.3 keeps a 1.1m doorway open; see the note above. */
const CELL = 0.3;
/** How far a cell centre must be from solid geometry. Under the capsule radius. */
const CLEARANCE = 0.26;
/**
 * The vertical band a standing guard occupies, measured from the surface.
 * Starting at 28cm rather than 0 means a kerb, a threshold or a floor trim is
 * not an obstacle — the character controller autosteps 42cm.
 */
const HEAD_LO = 0.28;
const HEAD_HI = 1.62;
/** Largest height difference two neighbouring cells may have and still connect. */
const STEP_UP = 0.45;
/** Most walkable surfaces stacked over one cell. Ground, gallery, and slack. */
const MAX_LAYERS = 4;
/** Two surfaces closer than this are the same surface. */
const MERGE_Y = 0.2;

/** Stops a hunt for an unreachable target from walking the whole building. */
const MAX_EXPANSIONS = 90_000;
/**
 * Heuristic weight. Above 1 the search is no longer guaranteed to return the
 * shortest route, and that is the point.
 *
 * Straight-line distance is a terrible guide in a two-storey building: a guard
 * on the ground floor standing under the far end of the gallery is four metres
 * from the goal as the crow flies and ninety along the only route, so plain A*
 * fans out across the entire ground floor before it will consider walking
 * BACKWARDS to the escalator. Weighting the heuristic collapses that fan by
 * roughly an order of magnitude; the cost is a route that is occasionally a
 * couple of metres longer than optimal, which nobody has ever noticed in a
 * patrolling guard.
 */
const H_WEIGHT = 1.6;
/** How far ahead the path smoother looks, in grid steps. Bounds its cost. */
const SMOOTH_WINDOW = 48;

const DIRS: Array<[number, number, number]> = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, Math.SQRT2],
  [1, -1, Math.SQRT2],
  [-1, 1, Math.SQRT2],
  [-1, -1, Math.SQRT2],
];

export interface NavStats {
  cells: number;
  nodes: number;
  blocked: number;
  buildMs: number;
}

export class NavGrid {
  readonly minX: number;
  readonly minZ: number;
  readonly nx: number;
  readonly nz: number;

  /** CSR index: the nodes of cell `i` are `[cellStart[i], cellStart[i + 1])`. */
  private readonly cellStart: Int32Array;
  private readonly nodeY: Float32Array;
  private readonly nodeCell: Int32Array;
  /**
   * How many colliders currently overlap this node. Zero is walkable.
   *
   * A count rather than a flag because barriers come and go: a shutter blocks
   * five cells that a wall may also block, and opening the shutter must not
   * clear the wall.
   */
  private readonly block: Int32Array;
  private readonly barrierCells = new Map<string, Int32Array>();
  private readonly openBarriers = new Set<string>();

  // A* scratch, allocated once. `stamp` avoids clearing 90k entries per query.
  private readonly gScore: Float32Array;
  private readonly fScore: Float32Array;
  private readonly cameFrom: Int32Array;
  private readonly seen: Int32Array;
  private readonly closed: Uint8Array;
  private stamp = 0;
  private heap: Int32Array;
  private heapLen = 0;

  /** Which island each node is in; -1 if blocked. See `label`. */
  private comp!: Int32Array;
  private compSizes: number[] = [];

  readonly stats: NavStats;
  /** Nodes expanded by the last `path` call. The number to watch when tuning. */
  lastExpansions = 0;

  constructor(
    zones: ZoneDef[],
    colliders: BoxCollider[],
    stairs: StairDef[],
    barriers: Array<BoxCollider & { id: string }>,
  ) {
    const t0 = now();

    let x0 = Infinity;
    let x1 = -Infinity;
    let z0 = Infinity;
    let z1 = -Infinity;
    for (const z of zones) {
      if (z.floor === 'none') continue;
      x0 = Math.min(x0, z.x0);
      x1 = Math.max(x1, z.x1);
      z0 = Math.min(z0, z.z0);
      z1 = Math.max(z1, z.z1);
    }
    this.minX = x0 - CELL;
    this.minZ = z0 - CELL;
    this.nx = Math.ceil((x1 - x0) / CELL) + 2;
    this.nz = Math.ceil((z1 - z0) / CELL) + 2;
    const cells = this.nx * this.nz;

    // --- pass 1: what surfaces exist over each cell -------------------------
    const cand = new Float32Array(cells * MAX_LAYERS);
    const count = new Uint8Array(cells);
    const push = (cell: number, y: number) => {
      const base = cell * MAX_LAYERS;
      const n = count[cell];
      for (let i = 0; i < n; i++) {
        if (Math.abs(cand[base + i] - y) < MERGE_Y) {
          // Keep the higher of two near-identical surfaces: the ramp lip should
          // win over the floor it lands on, never the other way round.
          if (y > cand[base + i]) cand[base + i] = y;
          return;
        }
      }
      if (n >= MAX_LAYERS) return;
      cand[base + n] = y;
      count[cell] = n + 1;
    };

    for (const z of zones) {
      if (z.floor === 'none') continue;
      const ci0 = this.cx(z.x0);
      const ci1 = this.cx(z.x1);
      const cj0 = this.cz(z.z0);
      const cj1 = this.cz(z.z1);
      for (let j = cj0; j <= cj1; j++) {
        for (let i = ci0; i <= ci1; i++) {
          const wx = this.wx(i);
          const wz = this.wz(j);
          if (wx < z.x0 || wx > z.x1 || wz < z.z0 || wz > z.z1) continue;
          push(j * this.nx + i, z.y);
        }
      }
    }

    for (const s of stairs) {
      const yaw = s.yaw ?? 0;
      const sin = Math.sin(yaw);
      const cos = Math.cos(yaw);
      const half = s.width / 2 + CELL;
      // Bounding box of the flight, generously, then an exact local test inside.
      const ex = Math.abs(sin) * s.run + half;
      const ez = Math.abs(cos) * s.run + half;
      const ci0 = this.cx(s.x - ex);
      const ci1 = this.cx(s.x + ex);
      const cj0 = this.cz(s.z - ez);
      const cj1 = this.cz(s.z + ez);
      for (let j = cj0; j <= cj1; j++) {
        for (let i = ci0; i <= ci1; i++) {
          const dx = this.wx(i) - s.x;
          const dz = this.wz(j) - s.z;
          const along = dx * sin + dz * cos;
          const side = dx * cos - dz * sin;
          if (along < -CELL || along > s.run + CELL) continue;
          if (Math.abs(side) > s.width / 2 + CLEARANCE) continue;
          const t = Math.min(1, Math.max(0, along / s.run));
          push(j * this.nx + i, s.y + s.rise * t);
        }
      }
    }

    // --- pass 2: flatten to CSR --------------------------------------------
    this.cellStart = new Int32Array(cells + 1);
    let total = 0;
    for (let c = 0; c < cells; c++) {
      this.cellStart[c] = total;
      total += count[c];
    }
    this.cellStart[cells] = total;

    this.nodeY = new Float32Array(total);
    this.nodeCell = new Int32Array(total);
    this.block = new Int32Array(total);
    for (let c = 0; c < cells; c++) {
      const n = count[c];
      if (!n) continue;
      const base = c * MAX_LAYERS;
      const slice: number[] = [];
      for (let i = 0; i < n; i++) slice.push(cand[base + i]);
      slice.sort((a, b) => a - b);
      for (let i = 0; i < n; i++) {
        this.nodeY[this.cellStart[c] + i] = slice[i];
        this.nodeCell[this.cellStart[c] + i] = c;
      }
    }

    // --- pass 3: what is in the way ----------------------------------------
    for (const c of colliders) {
      // Floor and ceiling slabs span the whole building and never intersect the
      // band a standing guard occupies over their own surface. Ramps ARE the
      // surface; their pitched slab's bounding box covers the entire rise, so
      // treating one as an obstacle would wall off its own staircase.
      const id = c.id ?? '';
      if (id.startsWith('floor_') || id.startsWith('ceil_') || id.startsWith('stair')) continue;
      this.applyCollider(c, null);
    }
    for (const b of barriers) this.applyCollider(b, b.id);

    let blocked = 0;
    for (let i = 0; i < total; i++) if (this.block[i] > 0) blocked++;

    this.gScore = new Float32Array(total);
    this.fScore = new Float32Array(total);
    this.cameFrom = new Int32Array(total);
    this.seen = new Int32Array(total);
    this.closed = new Uint8Array(total);
    this.heap = new Int32Array(Math.max(1024, total >> 3));
    this.comp = new Int32Array(total);
    this.label();

    this.stats = { cells, nodes: total, blocked, buildMs: Math.round(now() - t0) };
  }

  // --- geometry ------------------------------------------------------------

  private cx(x: number): number {
    return Math.min(this.nx - 1, Math.max(0, Math.round((x - this.minX) / CELL)));
  }
  private cz(z: number): number {
    return Math.min(this.nz - 1, Math.max(0, Math.round((z - this.minZ) / CELL)));
  }
  private wx(i: number): number {
    return this.minX + i * CELL;
  }
  private wz(j: number): number {
    return this.minZ + j * CELL;
  }

  /**
   * Marks every node this box stands in. `owner` names a barrier so its
   * contribution can be taken back when it opens.
   */
  private applyCollider(c: BoxCollider, owner: string | null): void {
    const yaw = c.yaw ?? 0;
    const sin = Math.sin(yaw);
    const cos = Math.cos(yaw);
    // Yaw-aligned half extents projected onto world axes, plus clearance.
    const ex = Math.abs(cos) * c.hx + Math.abs(sin) * c.hz + CLEARANCE;
    const ez = Math.abs(sin) * c.hx + Math.abs(cos) * c.hz + CLEARANCE;
    const lo = c.y - c.hy;
    const hi = c.y + c.hy;

    const touched: number[] = [];
    const ci0 = this.cx(c.x - ex);
    const ci1 = this.cx(c.x + ex);
    const cj0 = this.cz(c.z - ez);
    const cj1 = this.cz(c.z + ez);
    for (let j = cj0; j <= cj1; j++) {
      for (let i = ci0; i <= ci1; i++) {
        const dx = this.wx(i) - c.x;
        const dz = this.wz(j) - c.z;
        // Into the box's own frame. Inflating in local axes squares off the
        // corners, which errs toward calling a cell blocked — the safe way.
        const lx = dx * cos - dz * sin;
        const lz = dx * sin + dz * cos;
        if (Math.abs(lx) > c.hx + CLEARANCE || Math.abs(lz) > c.hz + CLEARANCE) continue;
        const cell = j * this.nx + i;
        for (let n = this.cellStart[cell]; n < this.cellStart[cell + 1]; n++) {
          const y = this.nodeY[n];
          if (hi <= y + HEAD_LO || lo >= y + HEAD_HI) continue;
          this.block[n]++;
          if (owner) touched.push(n);
        }
      }
    }
    if (owner) {
      const prev = this.barrierCells.get(owner);
      this.barrierCells.set(
        owner,
        prev ? Int32Array.from([...prev, ...touched]) : Int32Array.from(touched),
      );
    }
  }

  /** A breached shutter stops being a wall. Idempotent. */
  setBarrierOpen(id: string, open: boolean): void {
    const cells = this.barrierCells.get(id);
    if (!cells) return;
    const isOpen = this.openBarriers.has(id);
    if (isOpen === open) return;
    const delta = open ? -1 : 1;
    for (let i = 0; i < cells.length; i++) this.block[cells[i]] += delta;
    if (open) this.openBarriers.add(id);
    else this.openBarriers.delete(id);
    // A shutter is the one thing that changes what connects to what, so the
    // labelling has to be redone. It costs a couple of milliseconds and happens
    // a handful of times a round, which is a much better trade than making
    // every path query pay for a possible answer of "you cannot get there".
    this.label();
  }

  /**
   * Labels every unblocked node with the island it belongs to.
   *
   * Paid once at build and again whenever a shutter moves, so that `path` can
   * answer "no route exists" by comparing two integers instead of expanding
   * seventy thousand nodes across the whole ground floor first. That case is not
   * hypothetical — a guard ordered to a spot inside a locked shop hits it on
   * every single tick until the order changes.
   */
  private label(): void {
    const comp = this.comp;
    comp.fill(-1);
    const stack: number[] = [];
    const sizes: number[] = [];
    for (let s = 0; s < comp.length; s++) {
      if (comp[s] >= 0 || this.block[s] > 0) continue;
      const id = sizes.length;
      let size = 0;
      comp[s] = id;
      stack.push(s);
      while (stack.length) {
        const cur = stack.pop()!;
        size++;
        const cell = this.nodeCell[cur];
        const ci = cell % this.nx;
        const cj = (cell / this.nx) | 0;
        const cy = this.nodeY[cur];
        for (const [dx, dz] of DIRS) {
          const ni = ci + dx;
          const nj = cj + dz;
          if (ni < 0 || ni >= this.nx || nj < 0 || nj >= this.nz) continue;
          const ncell = nj * this.nx + ni;
          for (let n = this.cellStart[ncell]; n < this.cellStart[ncell + 1]; n++) {
            if (comp[n] >= 0 || this.block[n] > 0) continue;
            if (Math.abs(this.nodeY[n] - cy) > STEP_UP) continue;
            comp[n] = id;
            stack.push(n);
          }
        }
      }
      sizes.push(size);
    }
    this.compSizes = sizes;
  }

  /**
   * Island sizes, largest first — the level-design check.
   *
   * A finished building is one island plus a scattering of tiny ones (the inside
   * of a planter, the top of a counter). A second island in the thousands is a
   * room somebody sealed off by accident.
   */
  islands(min = 40): Array<{ nodes: number; at: [number, number, number] }> {
    const first = new Map<number, number>();
    for (let n = 0; n < this.comp.length; n++) {
      const c = this.comp[n];
      if (c >= 0 && !first.has(c)) first.set(c, n);
    }
    return this.compSizes
      .map((nodes, id) => ({ nodes, id }))
      .filter((r) => r.nodes >= min)
      .sort((a, b) => b.nodes - a.nodes)
      .map((r) => {
        const p = this.position(first.get(r.id)!);
        return {
          nodes: r.nodes,
          at: [Number(p.x.toFixed(1)), Number(p.y.toFixed(1)), Number(p.z.toFixed(1))] as [
            number,
            number,
            number,
          ],
        };
      });
  }

  // --- queries -------------------------------------------------------------

  /**
   * The node a position stands on, or -1.
   *
   * Spirals outward, because the caller is usually a physics capsule whose
   * centre may be a few centimetres inside a wall the grid calls blocked, and
   * refusing to path at all in that case is how an agent gets stuck forever.
   */
  find(pos: Vec3, maxRings = 8): number {
    const ci = this.cx(pos.x);
    const cj = this.cz(pos.z);
    let best = -1;
    let bestScore = Infinity;
    for (let ring = 0; ring <= maxRings; ring++) {
      for (let j = cj - ring; j <= cj + ring; j++) {
        if (j < 0 || j >= this.nz) continue;
        for (let i = ci - ring; i <= ci + ring; i++) {
          // Only the shell of each ring; the interior was covered already.
          if (ring > 0 && Math.abs(i - ci) !== ring && Math.abs(j - cj) !== ring) continue;
          if (i < 0 || i >= this.nx) continue;
          const cell = j * this.nx + i;
          for (let n = this.cellStart[cell]; n < this.cellStart[cell + 1]; n++) {
            if (this.block[n] > 0) continue;
            // Vertical error dominates: being on the wrong FLOOR is a much worse
            // answer than being a metre off along it.
            const dy = Math.abs(this.nodeY[n] - pos.y);
            const dx = this.wx(i) - pos.x;
            const dz = this.wz(j) - pos.z;
            const score = dx * dx + dz * dz + dy * dy * 9;
            if (score < bestScore) {
              bestScore = score;
              best = n;
            }
          }
        }
      }
      if (best >= 0) return best;
    }
    return best;
  }

  position(node: number, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
    const cell = this.nodeCell[node];
    out.x = this.wx(cell % this.nx);
    out.y = this.nodeY[node];
    out.z = this.wz((cell / this.nx) | 0);
    return out;
  }

  walkable(pos: Vec3): boolean {
    return this.find(pos, 0) >= 0;
  }

  /**
   * Can an agent walk the straight line between two points?
   *
   * Used to string-pull the grid path back into something that does not look
   * like it was drawn on graph paper. Samples at the grid pitch and demands an
   * unblocked node at roughly the interpolated height at every step, so it will
   * not cut the corner off a balcony.
   */
  losWalk(a: Vec3, b: Vec3): boolean {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    const steps = Math.ceil(len / (CELL * 0.9));
    if (steps === 0) return true;
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const p = { x: a.x + dx * t, y: a.y + (b.y - a.y) * t, z: a.z + dz * t };
      const n = this.find(p, 0);
      if (n < 0 || Math.abs(this.nodeY[n] - p.y) > STEP_UP) return false;
    }
    return true;
  }

  /**
   * A* between two world points, written into `out` as a list of waypoints.
   *
   * Returns false when the goal could not be reached, but STILL fills `out` with
   * the route to the closest node it found — unless the two are on different
   * islands, in which case `out` is left empty and the caller should choose a
   * different goal rather than trudge up to a locked shutter forever.
   */
  path(from: Vec3, to: Vec3, out: Vec3[]): boolean {
    out.length = 0;
    this.lastExpansions = 0;
    const start = this.find(from);
    const goal = this.find(to);
    if (start < 0 || goal < 0) return false;
    if (this.comp[start] !== this.comp[goal]) return false;
    if (start === goal) {
      out.push(this.position(goal));
      return true;
    }

    const stamp = ++this.stamp;
    this.heapLen = 0;
    this.gScore[start] = 0;
    this.fScore[start] = this.h(start, goal) * H_WEIGHT;
    this.cameFrom[start] = -1;
    this.seen[start] = stamp;
    this.closed[start] = 0;
    this.heapPush(start);

    let nearest = start;
    let nearestH = this.h(start, goal);
    let expansions = 0;
    let found = false;

    while (this.heapLen > 0) {
      const cur = this.heapPop();
      if (cur === goal) {
        found = true;
        break;
      }
      if (this.closed[cur]) continue;
      this.closed[cur] = 1;
      if (++expansions > MAX_EXPANSIONS) break;

      const cell = this.nodeCell[cur];
      const ci = cell % this.nx;
      const cj = (cell / this.nx) | 0;
      const cy = this.nodeY[cur];

      for (const [dx, dz, cost] of DIRS) {
        const ni = ci + dx;
        const nj = cj + dz;
        if (ni < 0 || ni >= this.nx || nj < 0 || nj >= this.nz) continue;
        // No squeezing through the diagonal gap between two blocked cells.
        if (dx !== 0 && dz !== 0) {
          if (!this.hasStep(cj * this.nx + ni, cy)) continue;
          if (!this.hasStep(nj * this.nx + ci, cy)) continue;
        }
        const ncell = nj * this.nx + ni;
        for (let n = this.cellStart[ncell]; n < this.cellStart[ncell + 1]; n++) {
          if (this.block[n] > 0) continue;
          const dy = this.nodeY[n] - cy;
          if (Math.abs(dy) > STEP_UP) continue;
          if (this.closed[n] === 1 && this.seen[n] === stamp) continue;
          // Climbing costs more than walking, so a guard prefers the flat route
          // to the same place — which is also the faster one in practice.
          const step = cost * CELL + Math.abs(dy) * 1.8;
          const g = this.gScore[cur] + step;
          if (this.seen[n] === stamp && g >= this.gScore[n]) continue;
          this.seen[n] = stamp;
          this.closed[n] = 0;
          this.gScore[n] = g;
          this.cameFrom[n] = cur;
          const hv = this.h(n, goal);
          this.fScore[n] = g + hv * H_WEIGHT;
          if (hv < nearestH) {
            nearestH = hv;
            nearest = n;
          }
          this.heapPush(n);
        }
      }
    }

    this.lastExpansions = expansions;
    const end = found ? goal : nearest;
    const raw: number[] = [];
    for (let n = end; n >= 0; n = this.cameFrom[n]) {
      raw.push(n);
      if (n === start) break;
      if (this.seen[n] !== stamp) break;
    }
    raw.reverse();

    // String-pull: keep only the corners. Walking a grid path verbatim produces
    // a staircase of 30cm hops that reads as an agent with a stutter.
    //
    // Forward-scanning with a window rather than testing the far end first:
    // visibility is checked against a bounded lookahead, so smoothing a
    // hundred-metre path costs a few thousand samples instead of a few million.
    const pts = raw.map((n) => this.position(n));
    if (pts.length) {
      out.push(pts[0]);
      let i = 0;
      while (i < pts.length - 1) {
        const limit = Math.min(pts.length - 1, i + SMOOTH_WINDOW);
        let best = i + 1;
        for (let j = i + 2; j <= limit; j++) {
          if (!this.losWalk(pts[i], pts[j])) break;
          best = j;
        }
        out.push(pts[best]);
        i = best;
      }
    }
    return found;
  }

  /** Is there an unblocked node in this cell within a step of height `y`? */
  private hasStep(cell: number, y: number): boolean {
    for (let n = this.cellStart[cell]; n < this.cellStart[cell + 1]; n++) {
      if (this.block[n] > 0) continue;
      if (Math.abs(this.nodeY[n] - y) <= STEP_UP) return true;
    }
    return false;
  }

  private h(a: number, b: number): number {
    const ca = this.nodeCell[a];
    const cb = this.nodeCell[b];
    const dx = ((ca % this.nx) - (cb % this.nx)) * CELL;
    const dz = (((ca / this.nx) | 0) - ((cb / this.nx) | 0)) * CELL;
    const dy = this.nodeY[a] - this.nodeY[b];
    return Math.sqrt(dx * dx + dz * dz) + Math.abs(dy) * 1.8;
  }

  /**
   * How many nodes are reachable on foot from here.
   *
   * The QA check that matters: a mall where the gallery is unreachable because
   * one escalator landing is 20cm short still looks perfect in a screenshot, and
   * a guard would simply never go up there.
   */
  reachable(from: Vec3): { nodes: number; upper: number } {
    const start = this.find(from);
    if (start < 0) return { nodes: 0, upper: 0 };
    const seen = new Uint8Array(this.nodeY.length);
    const queue = [start];
    seen[start] = 1;
    let count = 0;
    let upper = 0;
    while (queue.length) {
      const cur = queue.pop()!;
      count++;
      if (this.nodeY[cur] > 3) upper++;
      const cell = this.nodeCell[cur];
      const ci = cell % this.nx;
      const cj = (cell / this.nx) | 0;
      const cy = this.nodeY[cur];
      for (const [dx, dz] of DIRS) {
        const ni = ci + dx;
        const nj = cj + dz;
        if (ni < 0 || ni >= this.nx || nj < 0 || nj >= this.nz) continue;
        const ncell = nj * this.nx + ni;
        for (let n = this.cellStart[ncell]; n < this.cellStart[ncell + 1]; n++) {
          if (seen[n] || this.block[n] > 0) continue;
          if (Math.abs(this.nodeY[n] - cy) > STEP_UP) continue;
          seen[n] = 1;
          queue.push(n);
        }
      }
    }
    return { nodes: count, upper };
  }

  // --- binary heap ---------------------------------------------------------

  private heapPush(n: number): void {
    if (this.heapLen === this.heap.length) {
      const bigger = new Int32Array(this.heap.length * 2);
      bigger.set(this.heap);
      this.heap = bigger;
    }
    let i = this.heapLen++;
    this.heap[i] = n;
    const f = this.fScore;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (f[this.heap[parent]] <= f[this.heap[i]]) break;
      const t = this.heap[parent];
      this.heap[parent] = this.heap[i];
      this.heap[i] = t;
      i = parent;
    }
  }

  private heapPop(): number {
    const top = this.heap[0];
    const last = this.heap[--this.heapLen];
    if (this.heapLen > 0) {
      this.heap[0] = last;
      const f = this.fScore;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let small = i;
        if (l < this.heapLen && f[this.heap[l]] < f[this.heap[small]]) small = l;
        if (r < this.heapLen && f[this.heap[r]] < f[this.heap[small]]) small = r;
        if (small === i) break;
        const t = this.heap[small];
        this.heap[small] = this.heap[i];
        this.heap[i] = t;
        i = small;
      }
    }
    return top;
  }
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
